import type { DeclarationTypes } from 'src/constants/declarations';
import type { SubscriptionDetails, SubscriptionType } from 'src/constants/subscription';

export enum DashboardWidgetType {
  LIST_DECLARATIONS = 'listDeclarations',
  LIST_CUSTOMS_BASES = 'listCustomsBases',
  CUSTOMS_WEATHER = 'customsWeather',
  LATEST_NOMENCLATURE_NEWS = 'latestNomenclatureNews',
  OFFICIAL_JOURNAL = 'officialJournal',
  PODCAST = 'podcast'
}

export enum ListCustomsBasesSource {
  AUDITED = 'audited',
  RECENT = 'recent',
  MIXED = 'mixed'
}

export type ListDeclarationsFilters = {
  searchText: string;
  status: string | null;
  tags: string[] | null;
  state: string | null;
  isDischargeable: boolean;
  companyId: string | null;
  [key: string]: unknown;
};

export type WidgetBaseConfig = {
  name?: string;
};

export type ListDeclarationsWidgetConfig = WidgetBaseConfig & {
  declarationType: DeclarationTypes;
  subtype: DeclarationTypes | null;
  size: number;
  dateRange: { from: string; to: string };
  filters: ListDeclarationsFilters;
};

export type ListCustomsBasesWidgetConfig = WidgetBaseConfig & {
  source: ListCustomsBasesSource;
  size: number;
};

export type LatestNomenclatureNewsWidgetConfig = WidgetBaseConfig & {
  dateFrom: string;
};

export type OfficialJournalWidgetConfig = WidgetBaseConfig & {
  dateRange: { from: string; to: string };
};

export type DashboardWidgetConfig =
  | ListDeclarationsWidgetConfig
  | ListCustomsBasesWidgetConfig
  | LatestNomenclatureNewsWidgetConfig
  | OfficialJournalWidgetConfig
  | WidgetBaseConfig;

export type DashboardWidgetInstance = {
  id: string;
  type: DashboardWidgetType;
  x: number;
  y: number;
  w: number;
  h: number;
  config: DashboardWidgetConfig;
};

export type DashboardWidgetPlacement = Partial<Pick<DashboardWidgetInstance, 'x' | 'y' | 'w' | 'h'>>;

export type WidgetDefinition = {
  type: DashboardWidgetType;
  labelKey: string;
  defaultWidth: number;
  defaultHeight: number;
  minWidth?: number;
  minHeight?: number;
  maxHeight?: number;
  maxWidth?: number;
  onlyOneInstance?: boolean;
  hasConfiguration: boolean;
  createDefaultConfig: () => DashboardWidgetConfig;
};

export type WidgetSubtypeDefinition<T extends string | number = string> = {
  value: T;
  labelKey: string;
  tier?: SubscriptionDetails;
  subscriptionType?: SubscriptionType;
};

export type WidgetSubtypeOption<T extends string | number = string> = WidgetSubtypeDefinition<T> & {
  label: string;
  disable?: boolean;
};

export type DashboardCustomsBasesParams = {
  source: ListCustomsBasesSource | string;
  limit?: number;
  companyId?: string | null;
};

export type DashboardCustomsBasesQuery = {
  params: DashboardCustomsBasesParams;
  force?: boolean;
};

export type DashboardDeclarationsQuery = {
  params: Record<string, unknown>;
  force?: boolean;
};

export type RssFeedConfig = {
  origin: string;
  topic: string;
  url: string;
};

export type RssArticle = {
  title: string;
  link: string;
  pubDate: Date;
  feedTopic: string;
  feedOrigin: string;
};

export type OjEurLexDocument = {
  reference: string;
  publ_date: string;
  title?: string;
  swift_url: string;
};

export type OjEurLexSubjectBucket = {
  match: OjEurLexDocument[];
  dont_match: OjEurLexDocument[];
};

export type OjEurLexCategorizedBucket = {
  match: Record<string, OjEurLexDocument[]>;
  dont_match: Record<string, OjEurLexDocument[]>;
};

export type OjEurLexDocuments = {
  antidumping: OjEurLexSubjectBucket;
  other_subject: OjEurLexCategorizedBucket;
};
